import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  LinearProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/styles";
import { getBod2GapDetail, getBod3GapDetail } from "redux/actions/dashboard";

const useStyles = makeStyles({
  dialogTitle: {
    background: "#00abbd",
    color: "#fff",
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  titleText: {
    display: "flex",
    flexDirection: "column",
  },
  closeButton: {
    color: "#fff",
    padding: 4,
  },
  dialogContent: {
    padding: 0,
  },
  tableHead: {
    backgroundColor: "#f5f5f5",
  },
  tableHeadCell: {
    fontWeight: "bold",
    color: "#555",
    fontSize: 13,
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f0fbfc",
    },
  },
  rankCell: {
    color: "#aaa",
    fontWeight: "bold",
    fontSize: 13,
    width: 48,
  },
  nameCell: {
    fontWeight: 500,
    fontSize: 13,
  },
  usernameCell: {
    color: "#888",
    fontSize: 12,
  },
  gapChip: {
    fontWeight: "bold",
    fontSize: 12,
    minWidth: 60,
  },
  summaryBar: {
    background: "#f9f9f9",
    borderBottom: "1px solid #eee",
    padding: "10px 24px",
    display: "flex",
    gap: 24,
    alignItems: "center",
  },
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  emptyState: {
    textAlign: "center",
    color: "#aaa",
    padding: "60px 24px",
  },
  pagination: {
    borderTop: "1px solid #eee",
  },
});

// const getGapColor = (totalGap) => {
//   if (totalGap >= 80) return { backgroundColor: "#e8f5e9", color: "#2e7d32" };
//   if (totalGap >= 60) return { backgroundColor: "#fff8e1", color: "#f57f17" };
//   return { backgroundColor: "#fce4ec", color: "#c62828" };
// };

export default function GapDetailModal({
  open,
  onClose,
  categoryId,
  categoryName,
  bodLevel,
  projectId,
  projectName,
}) {
  const dispatch = useDispatch();
  const classes = useStyles();

  const [initialLoading, setInitialLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchDetail = useCallback(
    async (pg, limit, isInitial = false) => {
      if (!categoryId) return;

      if (isInitial) {
        setInitialLoading(true);
      } else {
        setPageLoading(true);
      }

      try {
        const action = bodLevel === 2 ? getBod2GapDetail : getBod3GapDetail;
        const result = await dispatch(
          action({
            categoryId,
            ...(projectId ? { projectId } : {}),
            page: pg + 1,
            limit,
          }),
        );
        if (result) {
          setData(result);
        } else {
          setData(null);
        }
      } catch (err) {
        setData(null);
      } finally {
        setInitialLoading(false);
        setPageLoading(false);
      }
    },
    [categoryId, bodLevel, projectId, dispatch],
  );

  useEffect(() => {
    if (open && categoryId) {
      setPage(0);
      fetchDetail(0, rowsPerPage, true);
    }
  }, [open, categoryId]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    fetchDetail(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    fetchDetail(0, newLimit);
  };

  const handleClose = () => {
    setData(null);
    setPage(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* Header */}
      <div className={classes.dialogTitle}>
        <div className={classes.titleText}>
          <Typography
            variant="h6"
            style={{ color: "#fff", fontWeight: "bold", lineHeight: 1.2 }}
          >
            {categoryName || "Detail Gap"}
          </Typography>
          <Typography
            variant="caption"
            style={{ color: "rgba(255,255,255,0.8)", marginTop: 2 }}
          >
            Kompetensi BOD-{bodLevel}
          </Typography>
        </div>
        <IconButton className={classes.closeButton} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>

      {/* Summary bar */}
      {data && !initialLoading && (
        <div className={classes.summaryBar}>
          {projectId && (
            <Chip
              label={
                projectName ? `Project: ${projectName}` : "Filtered by Project"
              }
              size="small"
              style={{
                backgroundColor: "#e0f7fa",
                color: "#00838f",
                fontSize: 11,
              }}
            />
          )}
        </div>
      )}

      {/* Content */}
      <DialogContent
        className={classes.dialogContent}
        style={{ maxHeight: "60vh", overflowY: "auto" }}
      >
        {initialLoading ? (
          <div className={classes.loadingWrapper}>
            <CircularProgress style={{ color: "#00abbd" }} />
          </div>
        ) : !data || data.data.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="body1">Tidak ada data</Typography>
          </div>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            {/* {pageLoading && (
              <LinearProgress style={{ backgroundColor: "#00abbd" }} />
            )} */}
            <Table
              size="small"
              style={{
                opacity: pageLoading ? 0.5 : 1,
                transition: "opacity 0.2s",
              }}
            >
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableHeadCell} align="center">
                    #
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>Nama</TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Username
                  </TableCell>
                  <TableCell className={classes.tableHeadCell} align="center">
                    Total Gap
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.data.map((person, index) => (
                  <TableRow key={person.id} className={classes.tableRow}>
                    <TableCell className={classes.rankCell} align="center">
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell className={classes.nameCell}>
                      {person.name}
                    </TableCell>
                    <TableCell className={classes.usernameCell}>
                      {person.username}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${person.totalGap}`}
                        size="small"
                        className={classes.gapChip}
                        // style={getGapColor(person.totalGap)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      {/* Pagination */}
      {data && data.totalData > 0 && !initialLoading && (
        <TablePagination
          className={classes.pagination}
          component="div"
          count={data.totalData}
          page={page}
          onChangePage={handleChangePage}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Baris per halaman:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} dari ${count}`
          }
        />
      )}

      <DialogActions
        style={{ borderTop: "1px solid #eee", padding: "8px 16px" }}
      >
        <Button
          onClick={handleClose}
          style={{ color: "#00abbd", fontWeight: "bold" }}
        >
          Tutup
        </Button>
      </DialogActions>
    </Dialog>
  );
}
